import { Container } from "./Container";
import { SubscribeForm } from "./SubscribeForm";
import { getConfig } from "../config/env";
import { HOME_SECTIONS, ROUTE_PATHS } from "../lib/routes";
import { sellerSubmitUrl } from "../lib/seller";
import styles from "./SiteFooter.module.css";
import { ThemeToggle } from "../theme/ThemeToggle";

/**
 * The subscribe card and the footer, as the canvas closes the page.
 *
 * `WEB-013` builds the card to the canvas: a `--surface` panel at 12px radius inside a
 * hairline border, 28px/34px of padding, a wrapping flex row with a 22px "Subscribe" over
 * one muted line on the left and the field beside its button on the right.
 *
 * The canvas's form is an address and a button. This one also carries the consent checkbox,
 * which is not a styling choice: `contracts/API.md` § Public subscription shape has core
 * store the consent sentence verbatim beside the address, and a consent record for a
 * sentence nobody was shown is not a consent record. It is the one departure, and it is
 * recorded as departure 7 in `contracts/design/web-homepage/README.md`.
 *
 * Email capture lives here rather than in the hero for the same reason the hero moved: the
 * page's job is to get a buyer to today's board in one screen, and a form competing with it
 * would cost more attention than it earns. Core's `source` allow-list is what makes this a
 * real question later — a delivery task can ask which surface produced subscribers worth
 * emailing, and `web_footer` is an honest answer only if the form is actually here.
 *
 * The colour-theme control closes the footer's link line (`WEB-008`). It was in the
 * masthead, where it was competing with the nav for width on a phone; here it can show all
 * three preferences at once instead of hiding two behind a picker.
 */
export function SiteFooter() {
  const { environment, apiBaseUrl } = getConfig();

  return (
    <footer className={styles.footer}>
      <Container>
        {/*
          The canvas's subscribe card. The pitch is handed to the form rather than placed
          beside it (`WEB-035`): the drawing is one row — pitch, 260px field, button — and
          the consent sentence canon requires (departure 7) has to wrap under the whole card
          rather than dictate the width of a column. With the pitch outside the form it did
          the opposite, and the card wrapped into two rows with the field stretched across
          838px of it.
        */}
        <div id="subscribe" className={styles.subscribe}>
          <SubscribeForm
            source="web_footer"
            labelledExternally
            inline
            pitch={
              <>
                <h2 className={styles.subscribeTitle}>Subscribe</h2>
                <p className={styles.subscribeLede}>
                  One email per day, when the board resets. Nothing else.
                </p>
              </>
            }
          />
        </div>

        {/*
          Canon § 12 again, at the foot of the page. The board's own brass band is the
          disclosure that matters; this is the standing policy statement a reader can find
          from anywhere, which is why the footer names it as a policy rather than repeating
          the band's sentence.
        */}
        <p className={styles.disclosure}>
          Dealbid&rsquo;s daily Top 10 is sponsored placement: sellers bid for the next
          day&rsquo;s positions. Rank reflects the winning bid, not an editorial judgement of
          which deal is best. Weekly and monthly lists are ranked by qualified buyer clicks
          and cannot be bought.
        </p>

        <div className={styles.bottom}>
          <p className={styles.links}>
            <span className={styles.copyright}>© 2026 Dealbid</span>
            {/*
              The policy page the paragraph above states in full (`WEB-018`). It sits
              directly after the copyright because that is where the canvas puts the policy
              links; `WEB-020` closes the rest of that line once Terms and Privacy exist.
            */}
            <a href={ROUTE_PATHS.disclosure}>Disclosure policy</a>
            <a href={ROUTE_PATHS.privacy}>Privacy</a>
            <a href={HOME_SECTIONS.howItWorks}>How it works</a>
            <a href={HOME_SECTIONS.whatIsDealbid}>What is Dealbid?</a>
            <a href={sellerSubmitUrl()}>Submit a deal</a>
          </p>
          <ThemeToggle />
        </div>

        {environment === "production" ? null : (
          <p className={styles.build}>
            Environment: {environment} · API: {apiBaseUrl}
          </p>
        )}
      </Container>
    </footer>
  );
}
